import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Check, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import {
  reviewOdontogram,
  trackContextualAIEvent,
  updateContextualAnalysisStatus,
} from "../../services/contextualAiApi";


export default function OdontogramAIReviewCard({
  patientId,
  existingAnalysis,
  onAnalysisChange,
  onClose,
}) {
  const [analysis, setAnalysis] = useState(existingAnalysis || null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const tracked = useRef(false);

  useEffect(() => setAnalysis(existingAnalysis || null), [existingAnalysis]);
  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    trackContextualAIEvent("ai_context_action_shown", {
      module: "odontogram",
      action: "structured_review",
      metadata: { context_type: "odontogram" },
    });
  }, []);

  const status = analysis?.status || "generated";
  const payload = analysis?.payload || {};
  const needsGeneration = !analysis || status === "stale" || status === "dismissed";

  const publish = (next) => {
    setAnalysis(next);
    onAnalysisChange?.(next);
  };

  const runReview = async () => {
    if (loading) return;
    setLoading(true);
    setMessage(null);
    const startedAt = Date.now();
    trackContextualAIEvent("ai_context_action_clicked", {
      module: "odontogram",
      action: "structured_review",
      metadata: { context_type: "odontogram" },
    });
    try {
      const result = await reviewOdontogram(patientId);
      publish(result);
      trackContextualAIEvent("ai_context_generation_success", {
        module: "odontogram",
        action: "structured_review",
        outcome: result?.payload?.generation_mode || "rules_only",
        duration_ms: Date.now() - startedAt,
        analysis_id: result?.id,
        metadata: { context_type: "odontogram", status: result?.status },
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.response?.data?.detail || "Não foi possível revisar o odontograma agora.",
      });
      trackContextualAIEvent("ai_context_generation_failed", {
        module: "odontogram",
        action: "structured_review",
        outcome: "error",
        duration_ms: Date.now() - startedAt,
        metadata: {
          context_type: "odontogram",
          error_code: String(error?.response?.status || "request_failed"),
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (nextStatus) => {
    if (!analysis?.id || loading) return;
    setLoading(true);
    try {
      const updated = await updateContextualAnalysisStatus(analysis.id, nextStatus);
      publish(updated);
      trackContextualAIEvent(
        nextStatus === "accepted" ? "ai_context_result_accepted" : "ai_context_result_dismissed",
        {
          module: "odontogram",
          action: "structured_review",
          analysis_id: analysis.id,
          metadata: { context_type: "odontogram", status: nextStatus },
        }
      );
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.detail || "Não foi possível atualizar a revisão." });
    } finally {
      setLoading(false);
    }
  };

  if (needsGeneration) {
    return (
      <div className="border-b border-purple-200 bg-purple-50 px-4 py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" />
            <div>
              <p className="font-semibold text-gray-900">
                {status === "stale" ? "O odontograma mudou desde a última revisão" : "Organize as marcações com IA"}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                A revisão considera somente os dados registrados. Nenhuma imagem será analisada.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border bg-white px-3 py-2 text-sm hover:bg-gray-50">Fechar</button>
            <button type="button" onClick={runReview} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {status === "stale" ? "Revisar novamente" : "Revisar marcações"}
            </button>
          </div>
        </div>
        {message && <p className="mt-3 text-sm text-red-700">{message.text}</p>}
      </div>
    );
  }

  const markedTeeth = Array.isArray(payload.marked_teeth) ? payload.marked_teeth : [];
  const consistencyItems = Array.isArray(payload.consistency_items) ? payload.consistency_items : [];

  return (
    <div className="border-b border-purple-200 bg-white px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-700" />
          <div>
            <p className="font-semibold text-gray-900">Revisão estruturada do odontograma</p>
            <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${status === "accepted" ? "bg-green-100 text-green-800" : "bg-purple-100 text-purple-800"}`}>
              {status === "accepted" ? "Revisado pela profissional" : "Aguardando conferência"}
            </span>
          </div>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={runReview} disabled={loading} className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-900">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Revisar novamente
          </button>
          <button type="button" onClick={onClose} aria-label="Fechar revisão"><X className="h-4 w-4 text-gray-500" /></button>
        </div>
      </div>

      <p className="mt-3 text-sm text-gray-700">{payload.summary}</p>
      {markedTeeth.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-gray-800">Marcações registradas</p>
          <ul className="mt-2 grid gap-1.5 text-sm text-gray-700 md:grid-cols-2">
            {markedTeeth.slice(0, 16).map((item) => <li key={item.tooth}><strong>Dente {item.tooth}:</strong> {item.description}</li>)}
          </ul>
          {markedTeeth.length > 16 && <p className="mt-2 text-xs text-gray-500">Mais {markedTeeth.length - 16} dente(s) com marcações.</p>}
        </div>
      )}
      {consistencyItems.length > 0 && (
        <div className="mt-4 rounded-lg bg-amber-50 p-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-900"><AlertCircle className="h-4 w-4" /> Pontos para confirmar</p>
          <ul className="mt-2 space-y-1 text-sm text-amber-900">{consistencyItems.map((item, index) => <li key={index}>• {item}</li>)}</ul>
        </div>
      )}
      {payload.ai_complement && (
        <div className="mt-4 rounded-lg bg-purple-50 p-3">
          <p className="text-sm font-semibold text-purple-900">Complemento da IA</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-purple-900">{payload.ai_complement}</p>
        </div>
      )}
      <p className="mt-4 text-xs text-gray-500">{payload.disclaimer}</p>
      {message && <p className="mt-3 text-sm text-red-700">{message.text}</p>}

      {status === "generated" && (
        <div className="mt-4 flex justify-end gap-2 border-t pt-3">
          <button type="button" onClick={() => changeStatus("dismissed")} disabled={loading} className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50"><X className="h-4 w-4" /> Descartar</button>
          <button type="button" onClick={() => changeStatus("accepted")} disabled={loading} className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700 disabled:opacity-60"><Check className="h-4 w-4" /> Marcar como revisado</button>
        </div>
      )}
    </div>
  );
}
